// Modelo del lienzo: dos columnas alineadas fila a fila. A la derecha la entidad (banda CLAVE, una
// sección por clase con sus campos conectados y, al final, los campos sin conectar); a la izquierda
// cada pregunta conectada UNA vez, colocada a la altura de sus campos (baricentro), y al final las
// preguntas sin conectar. Una arista por conexión (dto.maps), sin depender del número de
// formularios: qué formularios traen cada pregunta lo dicen los puntos de cobertura de su pastilla.
import { t } from '@vem/i18n.js';
import { aliasInfo, aliasLabel, entityByClass, extensionsOf, formAliasSets, formById, formName, keyRoleOf, questionType, shortName, catalog } from '@vem/catalog.js';
import { targetKey } from '@vem/mapOps.js';
import { isSys, typeFamily } from '@vem/recipe.js';
import { ALIAS_W, ALIAS_X, COV_DOTS_MAX, ENTITY_W, ENTITY_X, TOP_Y } from '@vem/layout.js';

const SYS_KEY = { '@quest_id': 'quest_id', '@response_id': 'response_id', '@response_date': 'response_date' };
const GLYPH = { text: 'glyph.text', int: 'glyph.int', dec: 'glyph.dec', bool: 'glyph.bool', date: 'glyph.date', dateAny: 'glyph.date', datetime: 'glyph.datetime' };

const norm = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Fuentes ordenadas por (prioridad, posición). */
export const orderedSources = (sources) => sources
  .map((s, i) => ({ s, i })).sort((a, b) => (a.s.priority || 1) - (b.s.priority || 1) || a.i - b.i).map((x) => x.s);

/** Etiqueta visible de una pregunta (o dato de la encuesta). */
export function aliasText(alias, sources) {
  if (isSys(alias)) return t('sysShort.' + SYS_KEY[alias]);
  return aliasLabel(alias, orderedSources(sources).map((s) => s.questId)) || alias;
}

/** Letra (A, B… Z, AA…) y color de una fuente: su posición en dto.sources, la misma en el carril y en el lienzo. */
export function formBadge(sources, questId) {
  let i = sources.findIndex((s) => s.questId === questId);
  const c = i % 8;
  let code = '';
  for (i += 1; i > 0; i = Math.floor((i - 1) / 26)) code = String.fromCharCode(65 + ((i - 1) % 26)) + code;
  return { code, color: `var(--fc${c})` };
}

/** Prefijo común a varios alias, hasta el último «_» ('' si hay menos de dos). */
export function commonPrefix(aliases) {
  if (aliases.length < 2) return '';
  const p = aliases.reduce((acc, a) => { let i = 0; while (i < acc.length && acc[i] === a[i]) i++; return acc.slice(0, i); });
  return p.slice(0, p.lastIndexOf('_') + 1);
}

/**
 * Alias conectados que no están en ningún formulario de la definición (activo o no): no se pintan como preguntas.
 * Vacío si falta el catálogo de algún formulario (no se sabe qué trae).
 */
export function orphanAliases(dto) {
  const sets = formAliasSets.value;
  if (dto.sources.some((s) => !sets.has(s.questId))) return new Set();
  return new Set(dto.maps.map((m) => m.sourceAlias).filter((a) => !isSys(a) && !dto.sources.some((s) => sets.get(s.questId).has(a))));
}

/** Cobertura de una pregunta: qué fuentes la traen. */
export function coverage(alias, sources) {
  const sets = formAliasSets.value;
  const list = orderedSources(sources);
  const live = list.filter((s) => s.enabled && sets.has(s.questId));
  const k = isSys(alias) ? live.length : live.filter((s) => sets.get(s.questId).has(alias)).length;
  const dots = list.map((s) => {
    const set = sets.get(s.questId);
    const state = !s.enabled || !set ? 'dis' : isSys(alias) || set.has(alias) ? 'on' : 'off';
    const b = formBadge(sources, s.questId);
    return { state, ...b, title: `${b.code} · P${s.priority || 1} ${formName(s.questId)}: ${t(state === 'on' ? 'em.formOn' : state === 'off' ? 'em.formOff' : 'lg.dis')}` };
  });
  return { k, N: live.length, dots: list.length <= COV_DOTS_MAX ? dots : null };
}

/**
 * @param {object} p
 * @param {object} p.dto definición en edición
 * @param {Map<string, {status:string, compact:object, line:string}>} p.recipes receta por campo (targetKey)
 * @param {{fieldsOpen:boolean, aliasesOpen:boolean, onlyWarnings:boolean, query:string, dragging:boolean}} p.view
 * @param {string|null} p.form questId del formulario cuyas preguntas se pintan (null = todas)
 * @returns {{nodes:object[], edges:object[], fieldOrder:string[], warnCount:number, matches:number}}
 */
export function buildCanvas({ dto, recipes, view, form = null }) {
  const root = dto.rootEntity;
  const sets = formAliasSets.value;
  const formSet = form ? sets.get(form) : null;
  const inForm = (a) => !formSet || isSys(a) || formSet.has(a);
  const classes = [root, ...extensionsOf(root)];
  const legacy = [...new Set(dto.maps.map((m) => m.entityClass))].filter((c) => !classes.includes(c));
  const orphans = orphanAliases(dto);
  const kept = dto.maps.filter((m) => !orphans.has(m.sourceAlias));
  const mapped = new Set(kept.map((m) => targetKey(m.entityClass, m.fieldName)));
  const statusOf = (id) => recipes.get(id)?.status || 'none';
  const bad = (id) => ['warn', 'danger'].includes(statusOf(id));
  const visible = (id) => !view.onlyWarnings || bad(id);
  const q = norm(view.query).trim();
  let matches = 0;
  const mark = (row, texts) => {
    if (!q) return row;
    const hit = texts.some((x) => norm(x).includes(q));
    if (hit) matches++;
    return Object.assign(row, hit ? { lit: true } : { dim: true });
  };
  const fieldsOf = (cls) => entityByClass(cls)?.fields || [];
  const isKey = (cls, f) => !!keyRoleOf(root, cls, f);
  const titleOf = (cls) => {
    const e = entityByClass(cls);
    return [e ? e.name : shortName(cls), e?.table, cls !== root && !legacy.includes(cls) ? '1:1' : null].filter(Boolean).join(' · ');
  };

  // ── Entidad ────────────────────────────────────────────────────────────────
  const E = [{ id: 'sec:key', kind: 'section', label: t('cv.key') }];
  const active = dto.sources.filter((s) => s.enabled);
  const byOrigin = active.filter((s) => s.keyMode !== 'entity');
  const byEntity = active.filter((s) => s.keyMode === 'entity');
  for (const role of byOrigin.length || !byEntity.length ? ['origin', 'id'] : []) {
    const e = entityByClass(root);
    const field = role === 'id' ? e?.sourceIdField : e?.originField;
    const vals = byOrigin.map((s) => (role === 'id' ? (s.idAlias ? `«${aliasText(s.idAlias, dto.sources)}»` : null)
      : s.originAlias ? `«${aliasText(s.originAlias, dto.sources)}»` : (s.originText || '').trim() ? t('key.fixed', { v: s.originText.trim() }) : null));
    const missing = vals.filter((v) => !v).length;
    const distinct = [...new Set(vals.filter(Boolean))];
    const value = missing ? t('cv.keyMissing', { n: missing }) : distinct.length > 1 ? t('cv.keyVaries', { n: distinct.length }) : distinct[0] || '—';
    const id = 'key:' + role;
    const row = { id, kind: 'key', role, label: field || '', value, warn: missing > 0, field: field ? targetKey(root, field) : null,
      title: `${t(role === 'id' ? 'key.id' : 'key.origin')} · ${value}` };
    if (!view.onlyWarnings || missing) E.push(mark(row, [field, value]));
  }
  if (byEntity.length) {
    // Formularios con clave por ID del registro: la pregunta que trae el ID (vacía = registro nuevo)
    const vals = byEntity.map((s) => (s.idAlias ? `«${aliasText(s.idAlias, dto.sources)}»` : null));
    const missing = vals.filter((v) => !v).length;
    const distinct = [...new Set(vals.filter(Boolean))];
    const value = missing ? t('cv.keyMissing', { n: missing }) : distinct.length > 1 ? t('cv.keyVaries', { n: distinct.length }) : distinct[0];
    const row = { id: 'key:entity', kind: 'key', role: 'entity', label: 'id', value, warn: missing > 0, field: null,
      title: `${t('ins.keyMode.entity')} · ${value}` };
    if (!view.onlyWarnings || missing) E.push(mark(row, ['id', value]));
  }
  let warnCount = 0;
  const fieldRow = (cls, f, connected) => {
    const id = targetKey(cls, f.name);
    const r = recipes.get(id);
    const fam = typeFamily(f.type);
    const chips = [];
    if (r && connected) {
      if (r.compact.rule) chips.push({ text: r.compact.rule, kind: 'rule' });
      if (r.compact.clear) chips.push({ text: r.compact.clear, kind: 'warn' });
      if (r.compact.cover) chips.push({ text: r.compact.cover, kind: 'cov', title: r.compact.coverTitle });
    }
    return mark({ id, kind: 'row', cls, field: f.name, label: f.name, connected, glyph: t(GLYPH[fam] || 'glyph.unsupported'),
      glyphTitle: f.type, chips, status: connected ? statusOf(id) : 'none', title: r && connected ? r.line : `${f.name} · ${f.type} · ${f.column}` },
    [f.name, f.column]);
  };
  for (const cls of [...classes, ...legacy]) {
    const conn = fieldsOf(cls).filter((f) => !isKey(cls, f.name) && mapped.has(targetKey(cls, f.name)));
    const isLegacy = legacy.includes(cls);
    const legacyFields = isLegacy ? [...new Set(dto.maps.filter((m) => m.entityClass === cls).map((m) => m.fieldName))] : [];
    conn.forEach((f) => { if (bad(targetKey(cls, f.name))) warnCount++; });
    const shown = conn.filter((f) => visible(targetKey(cls, f.name)));
    if (!shown.length && !isLegacy && view.onlyWarnings) continue;
    E.push({ id: 'sec:' + cls, kind: 'section', label: isLegacy ? t('cv.legacy', { name: shortName(cls), root: shortName(root) }) : titleOf(cls),
      count: t('cv.count', { n: conn.length }), legacy: isLegacy });
    shown.forEach((f) => E.push(fieldRow(cls, f, true)));
    if (isLegacy) {
      legacyFields.filter((n) => !conn.some((f) => f.name === n))
        .forEach((n) => E.push({ id: targetKey(cls, n), kind: 'row', cls, field: n, label: n, connected: true, glyph: '?', chips: [], status: 'danger', title: n }));
      E.push({ id: 'legacy:' + cls, kind: 'action', cls, label: t('cv.legacyRemove', { n: dto.maps.filter((m) => m.entityClass === cls).length }) });
    }
  }
  const free = classes.flatMap((cls) => fieldsOf(cls).filter((f) => !isKey(cls, f.name) && !mapped.has(targetKey(cls, f.name))).map((f) => ({ cls, f })));
  if (free.length && !view.onlyWarnings) {
    const open = view.fieldsOpen || view.dragging;
    E.push({ id: 'grp:fields', kind: 'group', label: t('cv.freeFields', { n: free.length }), open });
    if (open) {
      for (const cls of classes) {
        const mine = free.filter((x) => x.cls === cls);
        if (!mine.length) continue;
        E.push({ id: 'fsec:' + cls, kind: 'section', label: titleOf(cls), sub: true });
        mine.forEach((x) => E.push(fieldRow(cls, x.f, false)));
      }
    }
  }
  const rowOf = new Map(E.map((r, i) => [r.id, i]));

  // ── Preguntas conectadas, alineadas con sus campos ─────────────────────────
  const firstMap = new Map();
  dto.maps.forEach((m, i) => { if (!orphans.has(m.sourceAlias) && inForm(m.sourceAlias) && !firstMap.has(m.sourceAlias)) firstMap.set(m.sourceAlias, i); });
  const items = [...firstMap.keys()].map((a) => {
    const ys = kept.filter((m) => m.sourceAlias === a).map((m) => rowOf.get(targetKey(m.entityClass, m.fieldName))).filter((y) => y != null);
    return { a, ys, bary: ys.length ? ys.reduce((s, y) => s + y, 0) / ys.length : 0, first: Math.min(...ys), mi: firstMap.get(a) };
  }).filter((x) => x.ys.length).sort((x, y) => x.bary - y.bary || x.first - y.first || x.mi - y.mi);

  const keyTags = (alias) => {
    const tags = [];
    if (byOrigin.some((s) => s.idAlias === alias)) tags.push(t('chip.keyId'));
    if (byOrigin.some((s) => s.originAlias === alias)) tags.push(t('chip.keyOrigin'));
    if (byEntity.some((s) => s.idAlias === alias)) tags.push(t('src.keyEntity'));
    return tags;
  };
  const pool = new Set(formSet || []);
  if (!formSet) active.forEach((s) => (sets.get(s.questId) || []).forEach((a) => pool.add(a)));
  // El prefijo común a todos los alias no se repite en cada pastilla: lo dice la tarjeta de cada formulario del carril
  const prefix = commonPrefix([...new Set([...firstMap.keys(), ...pool])].filter((a) => !isSys(a)));
  const pill = (alias, connected) => {
    const sys = isSys(alias);
    const cov = coverage(alias, dto.sources);
    const dead = !sys && cov.N > 0 && cov.k === 0 && connected;
    // El alias es lo que se conecta (y lo único que existe si ningún formulario trae la pregunta): el texto va al título
    const text = aliasText(alias, dto.sources);
    const label = sys ? text : alias.slice(prefix.length) || alias;
    const fields = kept.filter((m) => m.sourceAlias === alias).map((m) => m.fieldName);
    const qt = sys ? null : questionType(aliasInfo(alias, orderedSources(dto.sources).map((s) => s.questId)));
    return mark({ id: alias, kind: 'pill', alias, label, sys, dead, connected, tags: sys ? [t('cv.tagSys')] : keyTags(alias), cov,
      glyph: qt?.glyph, glyphTitle: qt?.text,
      title: sys ? `${text} · ${alias}` : `${alias}${text !== alias ? ` · «${text}»` : ''}${qt ? ` · ${qt.text}` : ''} · ${t('fs.carried', { k: cov.k, N: cov.N })}`, fields }, [text, alias]);
  };

  // Arriba del todo, para que se vea: la fila 0 de la entidad es la sección CLAVE, que nunca lleva pregunta
  const A = orphans.size ? [{ id: 'orphans', kind: 'action', label: t('cv.orphans', { n: dto.maps.length - kept.length }),
    title: t('cv.orphansTitle', { list: [...orphans].join(', ') }) }] : [];
  let cursor = A.length;
  for (let i = 0; i < items.length;) {
    const w = Math.round(items[i].bary);
    let k = 1;
    while (i + k < items.length && Math.round(items[i + k].bary) === w) k++;
    const start = Math.max(cursor, w - Math.floor((k - 1) / 2));
    while (cursor < start) A.push({ id: 'gap:' + cursor, kind: 'gap', n: cursor++ });
    for (let j = 0; j < k; j++, cursor++) A.push(pill(items[i + j].a, true));
    i += k;
  }

  // ── Preguntas sin conectar ─────────────────────────────────────────────────
  const connectedSet = new Set(firstMap.keys());
  const freeSys = catalog.systemAliases.filter((a) => !connectedSet.has(a));
  const freeAliases = [...pool].filter((a) => !connectedSet.has(a))
    .map((a) => pill(a, false)).sort((x, y) => y.cov.k - x.cov.k || x.label.localeCompare(y.label));
  const nFree = freeSys.length + freeAliases.length;
  if (nFree && !view.onlyWarnings) {
    const open = view.aliasesOpen;
    A.push({ id: 'grp:aliases', kind: 'group', label: t('cv.freeAliases', { n: nFree }), open });
    if (open) {
      if (freeSys.length) {
        A.push({ id: 'asec:sys', kind: 'section', label: t('cv.sysGroup'), sub: true });
        freeSys.forEach((a) => A.push(pill(a, false)));
      }
      if (freeAliases.length) {
        A.push({ id: 'asec:q', kind: 'section', label: t('pk.questions'), sub: true });
        A.push(...freeAliases);
      }
    }
  }

  // ── Aristas: una por conexión ──────────────────────────────────────────────
  const pillIds = new Set(A.filter((r) => r.kind === 'pill').map((r) => r.id));
  const deadIds = new Set(A.filter((r) => r.kind === 'pill' && r.dead).map((r) => r.id));
  const edges = [];
  dto.maps.forEach((m, i) => {
    const to = targetKey(m.entityClass, m.fieldName);
    if (!rowOf.has(to) || !pillIds.has(m.sourceAlias)) return;
    edges.push({
      key: 'm' + i, index: i, from: { node: 'aliases', row: m.sourceAlias }, to: { node: 'entity', row: to },
      kind: isSys(m.sourceAlias) ? 'sys' : deadIds.has(m.sourceAlias) ? 'dead' : null,
    });
  });

  // Campos conectados solo desde preguntas de otros formularios: siguen conectados, pero sin línea aquí
  const reached = new Set(edges.map((e) => e.to.row));
  E.forEach((r) => {
    if (r.kind === 'row' && r.connected && !reached.has(r.id) && formSet) Object.assign(r, { other: true, title: `${t('cv.otherForms')} · ${r.title}` });
  });

  const entity = entityByClass(root);
  const shown = formSet ? formBadge(dto.sources, form) : null;
  const nodes = [
    { key: 'aliases', kind: 'aliases', x: ALIAS_X, y: TOP_Y, w: ALIAS_W, title: t('cv.aliases') + ' · ' + items.length,
      sub: shown ? t('cv.aliasesOfForm', { code: shown.code, name: formName(form) }) : t('cv.aliasesSub'), rows: A },
    { key: 'entity', kind: 'entity', x: ENTITY_X, y: TOP_Y, w: ENTITY_W, title: [root, ...extensionsOf(root)].map((c) => entityByClass(c)?.name || shortName(c)).join(' + '),
      sub: [root, ...extensionsOf(root)].map((c) => entityByClass(c)?.table).filter(Boolean).join(' · '), missing: !entity, rows: E },
  ];
  return {
    nodes, edges, warnCount, matches,
    fieldOrder: E.filter((r) => r.kind === 'row' || r.kind === 'key').map((r) => (r.kind === 'key' ? r.id : r.id)),
    hasForm: (questId) => !!formById(questId),
  };
}
